/** Closing and reopening a payroll month, pure so it tests without a renderer. The hooks that call `/api/payroll-months` are in `./payroll`.
 *  The CFO and accountant close a month (`salary.edit`, as the server gates); reopening is administrator-only and audited. */

/** One `GET /api/payroll-months` row. A month with no row is open. */
export type PayrollMonth = {
  year: number
  month: number
  status: 'Open' | 'Closed'
  closed_at: string | null
  closed_by_id: string | null
  closed_by_name: string | null
  note: string
  /** The days the PAYROLL month covers, both inclusive (ISO dates). */
  start: string
  end: string
}

/** What the payroll page offers for the month it shows.
 *  ⛔ `unknown` while the list has not loaded: NO control and NO "closed" sentence, since an unknown state can be described as neither.
 *  Closed offers `reopen` to the administrator only; open offers `close` to a `salary.edit` holder, but ONLY once the month's run has ended (`canClose`), because the page defaults to the month covering today and the likeliest first click would lock the month everyone still files time sheets against. The server enforces it too (409 `payroll_month_not_ended`).
 *  `runEndKnown` is separate from `canClose`: an unknown run end disables the CONTROL and makes the explaining SENTENCE absent, never an interpolated `undefined`. `runEnd` is only renderable when `runEndKnown`. */
type MonthCloseState =
  | { kind: 'unknown' }
  | { kind: 'open'; mayClose: boolean; canClose: boolean; runEndKnown: boolean; runEnd: string | undefined; blockedUntil: string | null }
  | { kind: 'closed'; entry: PayrollMonth; mayReopen: boolean }

export function monthCloseState(
  months: PayrollMonth[] | undefined,
  year: number,
  month: number,
  {
    mayClose,
    isAdmin,
    runEnd,
    today,
    blockedUntil,
  }: { mayClose: boolean; isAdmin: boolean; runEnd: string | undefined; today: string; blockedUntil: string | null },
): MonthCloseState {
  if (!months) return { kind: 'unknown' }
  const entry = months.find((m) => m.year === year && m.month === month)
  if (entry?.status === 'Closed') return { kind: 'closed', entry, mayReopen: isAdmin }
  const runEndKnown = runEnd !== undefined
  const canClose = runEnd !== undefined && today > runEnd && blockedUntil === null
  return { kind: 'open', mayClose, canClose, runEndKnown, runEnd, blockedUntil }
}

/** The server answers 200 with status `Closed` and no `closed_at` when a locked month was left unwritten, so only `closed_at` proves a close. */
export function closeWroteNothing(answer: Pick<PayrollMonth, 'closed_at'>): boolean {
  return answer.closed_at === null
}
